import React from 'react';
import MuiTooltip, { TooltipProps } from '@mui/material/Tooltip';
import { bluePlanetTheme } from 'ui/theme';
import { fontSizeMedium } from 'ui/theme/themeBluePlanet';

interface PlainTooltipProps {
  title: NonNullable<React.ReactNode>;
  placement?: TooltipProps['placement'];
  children: TooltipProps['children'];
  enterDelay?: number;
  arrow?: boolean;
}

export default function PlainTooltip({
  title,
  placement = 'top',
  children,
  enterDelay = 0,
  arrow = true,
}: PlainTooltipProps) {
  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow={arrow}
      enterDelay={enterDelay}
      slotProps={{
        popper: {
          popperOptions: {
            modifiers: [
              // Flip to the opposite side when the preferred one doesn't have room. Don't add
              // cross-axis fallbacks: MUI sizes the popper differently per axis (14px margin
              // below a top-placed tooltip vs. beside a side-placed one), so a top <-> right
              // candidate list makes each placement change the measurement that picked it,
              // and the tooltip oscillates between the two.
              { name: 'flip', options: { padding: 8 } },
              // Slide along the other axis so a tall tooltip stays inside the viewport
              // instead of staying pinned to the anchor and growing off screen.
              { name: 'preventOverflow', options: { altAxis: true, tether: false, padding: 8 } },
            ],
          },
        },
        tooltip: {
          sx: {
            backgroundColor: bluePlanetTheme.bluePlanetPalette.white,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${bluePlanetTheme.bluePlanetPalette.grey.light}`,
            borderRadius: '8px',
            padding: 0,
            maxWidth: 'none',
            color: bluePlanetTheme.bluePlanetPalette.indigo.main,
            fontSize: fontSizeMedium,
          },
        },
        arrow: {
          sx: {
            '&::before': {
              backgroundColor: bluePlanetTheme.bluePlanetPalette.white,
              border: `1px solid ${bluePlanetTheme.bluePlanetPalette.grey.light}`,
            },
          },
        },
      }}
    >
      <span>{children}</span>
    </MuiTooltip>
  );
}
